import { motion, AnimatePresence } from 'framer-motion';
import { AnswerProps, useDailyAnswerStore } from './useDailyAnswer';
import { useEffect, useState } from 'react';
import { Question } from './question';
import { SubmitQuestions } from './question/submit';
import { Loading } from './loading';
import { StartQuestions } from './question/start';
import { useAuthentication } from '@/hooks/useAuthentication';

export default function WeeklyQuestions({
  yourWeeklyquestionTitle,
  yourWeeklyQuestionOnboardingTitle,
  yourWeeklyQuestionDescription,
  onboardingQuestionTitle,
  onboardingQuestionDescription,
  onboardingQuestionButtonLabel,
}: {
  yourWeeklyquestionTitle?: string;
  yourWeeklyQuestionOnboardingTitle?: string;
  yourWeeklyQuestionDescription?: string | null;
  onboardingQuestionTitle?: string | null;
  onboardingQuestionDescription?: string | null;
  onboardingQuestionButtonLabel?: string | null;
}) {
  const { fetchQuestions, questions, questionLoading } = useDailyAnswerStore();
  const [selectedAnswer, setSelectedAnswer] = useState<AnswerProps[]>([]);
  const [submmited, setSubmmited] = useState(false);
  const [started, setStarted] = useState(false);
  const [t, setT] = useState(yourWeeklyQuestionOnboardingTitle);
  const { authentication } = useAuthentication();

  const addAnsweredQuestion = (answer: AnswerProps) => {
    setSelectedAnswer((prev) => [...prev, answer]);
  };

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  useEffect(() => {
    if (!authentication?.user) return;

    if (!started && authentication?.user?.onboardQuestionsCompleted) {
      setStarted(true);
    }
  }, [authentication?.user, started]);

  useEffect(() => {
    if (started && yourWeeklyquestionTitle) {
      setT(yourWeeklyquestionTitle);
    }
  }, [started, yourWeeklyquestionTitle]);

  if (!authentication?.user) return null;

  if (questionLoading) {
    return <Loading />;
  }

  if (questions?.length === 0) {
    return;
  }

  const allAnswered = questions?.length === selectedAnswer?.length;

  return (
    <>
      <h2 className={`${started ? '' : 'mb-sh-15'} font-larken !text-sh-md`}>{t}</h2>
      {yourWeeklyQuestionDescription && started && (
        <p className="mb-sh-15 font-larken text-sh-18-24">{yourWeeklyQuestionDescription}</p>
      )}
      <div className="gap-4 md:flex">
        <AnimatePresence mode="wait">
          {!started && (
            <StartQuestions
              selectedAnswer={selectedAnswer}
              startQuestions={setStarted}
              onboardingQuestionTitle={onboardingQuestionTitle}
              onboardingQuestionDescription={onboardingQuestionDescription}
              onboardingQuestionButtonLabel={onboardingQuestionButtonLabel}
            />
          )}
        </AnimatePresence>
        {started && (
          <>
            {allAnswered ? (
              <>
                <AnimatePresence mode="wait">
                  {!submmited && (
                    <motion.div
                      key={'submit'}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}>
                      <SubmitQuestions selectedAnswer={selectedAnswer} setFinished={setSubmmited} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            ) : (
              <>
                {questions.map((question, idx) => (
                  <>
                    <AnimatePresence mode="wait">
                      {question &&
                        !selectedAnswer.find(
                          (answeredQuestion) => answeredQuestion?.currentQuestion?.id === question.id,
                        ) && (
                          <motion.div
                            key={question.id}
                            initial={{ opacity: 1, y: 0 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}>
                            <Question
                              key={question.id}
                              index={idx + 1}
                              question={question}
                              setSelectedAnswer={addAnsweredQuestion}
                            />
                          </motion.div>
                        )}
                    </AnimatePresence>
                  </>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}
